import React, { useState, useRef, useEffect } from 'react';
import {
  Container,
  SpaceBetween,
  Textarea,
  Button,
  Box,
} from '@cloudscape-design/components';
import { apiService } from '../../services/api';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface ChatState {
  messages: ChatMessage[];
  isStreaming: boolean;
  currentStreamText: string;
  dataProduct: string | null;
  sessionId: string;
}

const INITIAL_GREETING = 'What kind of Live Map would you like to make?';

/** Detect which data product the assistant is discussing based on response keywords. */
function detectDataProduct(text: string): string | null {
  const lower = text.toLowerCase();
  // Order matters: check more specific patterns first
  if (lower.includes('metar') || lower.includes('aviation weather') || lower.includes('flight category')) return 'metar';
  if (lower.includes('marine') || lower.includes('buoy') || lower.includes('wave height') || lower.includes('sea condition')) return 'marine';
  if (lower.includes('hf propagation') || lower.includes('ham radio') || lower.includes('shortwave') || lower.includes('propagation')) return 'hf_propagation';
  if (lower.includes('trail') || lower.includes('grooming') || lower.includes('snow depth') || lower.includes('trail condition')) return 'trail';
  if (lower.includes('nws') || lower.includes('national weather service') || lower.includes('weather forecast')) return 'nws';
  return null;
}

const SESSION_ID_KEY = 'livemaps-chat-session-id';

function getOrCreateSessionId(): string {
  let sessionId = localStorage.getItem(SESSION_ID_KEY);
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    localStorage.setItem(SESSION_ID_KEY, sessionId);
  }
  return sessionId;
}

export const Chatbot: React.FC = () => {
  const [chatState, setChatState] = useState<ChatState>({
    messages: [],
    isStreaming: false,
    currentStreamText: '',
    dataProduct: null,
    sessionId: getOrCreateSessionId(),
  });
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatState.messages, chatState.currentStreamText]);

  const handleSend = async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || chatState.isStreaming) return;

    const userMessage: ChatMessage = { role: 'user', content: trimmed };
    // Do NOT send the initial greeting in history — the backend system prompt
    // already knows the user has been greeted. Sending it causes the LLM to
    // repeat the greeting question.
    const updatedMessages = [...chatState.messages, userMessage];

    setChatState((prev) => ({
      ...prev,
      messages: updatedMessages,
      isStreaming: true,
      currentStreamText: '',
    }));
    setInputValue('');

    let fullResponse = '';

    try {
      await apiService.streamChat(
        trimmed,
        chatState.sessionId,
        (chunk) => {
          fullResponse += chunk;
          setChatState((prev) => ({
            ...prev,
            currentStreamText: fullResponse,
          }));
        },
        chatState.dataProduct ?? undefined
      );

      // Detect data_product from assistant response for smart routing on next turn
      const detectedProduct = detectDataProduct(fullResponse);

      // Stream complete — add assistant message to history
      if (fullResponse) {
        setChatState((prev) => ({
          ...prev,
          messages: [...prev.messages, { role: 'assistant', content: fullResponse }],
          isStreaming: false,
          currentStreamText: '',
          dataProduct: detectedProduct ?? prev.dataProduct,
        }));
      } else {
        setChatState((prev) => ({
          ...prev,
          isStreaming: false,
          currentStreamText: '',
        }));
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred.';
      setChatState((prev) => ({
        ...prev,
        messages: [...prev.messages, { role: 'assistant', content: `Error: ${errorMessage}` }],
        isStreaming: false,
        currentStreamText: '',
      }));
    }
  };

  const handleRestart = () => {
    // Generate a new session_id for the fresh conversation
    const newSessionId = crypto.randomUUID();
    localStorage.setItem(SESSION_ID_KEY, newSessionId);
    setChatState({
      messages: [],
      isStreaming: false,
      currentStreamText: '',
      dataProduct: null,
      sessionId: newSessionId,
    });
    setInputValue('');
  };

  const handleKeyDown = (e: CustomEvent<{ key: string }>) => {
    if (e.detail.key === 'Enter') {
      handleSend();
    }
  };

  return (
    <Container>
      <SpaceBetween size="l">
        {/* Scrollable message history */}
        <div
          style={{
            maxHeight: '500px',
            overflowY: 'auto',
            padding: '8px 0',
          }}
        >
          {/* Initial greeting */}
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '12px' }}>
            <div
              style={{
                backgroundColor: '#f2f3f3',
                borderRadius: '12px',
                padding: '10px 16px',
                maxWidth: '75%',
              }}
            >
              <Box variant="p" color="text-body-secondary">
                <strong>Assistant</strong>
              </Box>
              <Box variant="p">{INITIAL_GREETING}</Box>
            </div>
          </div>

          {/* Message bubbles */}
          {chatState.messages.map((msg, idx) => {
            const isUser = msg.role === 'user';
            return (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  justifyContent: isUser ? 'flex-end' : 'flex-start',
                  marginBottom: '12px',
                }}
              >
                <div
                  style={{
                    backgroundColor: isUser ? '#0972d3' : '#f2f3f3',
                    color: isUser ? '#ffffff' : '#000716',
                    borderRadius: '12px',
                    padding: '10px 16px',
                    maxWidth: '75%',
                    wordBreak: 'break-word',
                  }}
                >
                  <Box variant="p" color={isUser ? 'inherit' : 'text-body-secondary'}>
                    <strong>{isUser ? 'You' : 'Assistant'}</strong>
                  </Box>
                  <div style={{ color: 'inherit', whiteSpace: 'pre-wrap' }}>{msg.content}</div>
                </div>
              </div>
            );
          })}

          {/* Streaming text indicator */}
          {chatState.isStreaming && (
            <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '12px' }}>
              <div
                style={{
                  backgroundColor: '#f2f3f3',
                  borderRadius: '12px',
                  padding: '10px 16px',
                  maxWidth: '75%',
                }}
              >
                <Box variant="p" color="text-body-secondary">
                  <strong>Assistant</strong>
                </Box>
                {chatState.currentStreamText ? (
                  <div style={{ whiteSpace: 'pre-wrap' }}>{chatState.currentStreamText}</div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 0' }}>
                    <span className="typing-dot" style={{ animationDelay: '0s' }} />
                    <span className="typing-dot" style={{ animationDelay: '0.2s' }} />
                    <span className="typing-dot" style={{ animationDelay: '0.4s' }} />
                  </div>
                )}
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <SpaceBetween direction="horizontal" size="xs">
          <div style={{ flex: 1 }}>
            <Textarea
              value={inputValue}
              onChange={({ detail }) => setInputValue(detail.value)}
              onKeyDown={handleKeyDown as any}
              placeholder="Type your message..."
              disabled={chatState.isStreaming}
              rows={2}
              autoFocus
            />
          </div>
          <Button
            variant="primary"
            onClick={handleSend}
            disabled={chatState.isStreaming || !inputValue.trim()}
          >
            Send
          </Button>
          <Button variant="link" onClick={handleRestart}>
            Restart
          </Button>
        </SpaceBetween>
      </SpaceBetween>

      {/* Typing indicator animation */}
      <style>{`
        .typing-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: #687078;
          animation: typing-bounce 1.4s infinite ease-in-out both;
          display: inline-block;
        }
        @keyframes typing-bounce {
          0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
          40% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </Container>
  );
};
